import React, { useState } from 'react';
import './aluminiumwindow.css';

import {
  FaDoorOpen,
  FaRegBuilding,
  FaShieldAlt,
  FaCompressArrowsAlt,
  FaLayerGroup,
  FaVolumeMute
} from 'react-icons/fa';

function GlassDoorGlassPartition() {
  const [activeTab, setActiveTab] = useState('types');
  const [selectedImage, setSelectedImage] = useState(0);

  const images = [
    '/glass door 1.webp',
    '/glass door 2.jpg',
    '/partition3.JPG',
    '/glass partition 5.webp',
    '/glass partition 4.jpg',
  ];

  const features = [
    {
      icon: <FaDoorOpen />,
      title: 'Sleek Glass Doors',
      description: 'Elegant frameless and framed glass doors that enhance modern interiors'
    },
    {
      icon: <FaLayerGroup />,
      title: 'Versatile Partitions',
      description: 'Glass partitions designed for offices, showrooms, and commercial interiors'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Safety Glass',
      description: 'Toughened and laminated glass ensuring strength and user safety'
    },
    {
      icon: <FaCompressArrowsAlt />,
      title: 'Space Enhancement',
      description: 'Creates open, bright spaces while maintaining separation and privacy'
    },
    {
      icon: <FaVolumeMute />,
      title: 'Acoustic Control',
      description: 'Specialized glass options for noise reduction and workplace comfort'
    },
    {
      icon: <FaRegBuilding />,
      title: 'Modern Aesthetics',
      description: 'Minimalist designs that complement contemporary architecture'
    }
  ];

  const glassTypes = [
    { name: 'Frameless Glass Doors', image: '/frameless glass door.jpg' },
    { name: 'Sliding Glass Doors', image: '/sliding door.webp' },
    { name: 'Glass Office Partitions', image: '/partition3.JPG' },
    { name: 'Frosted Glass Partitions', image: '/frosting.webp' },
  ];

  const specifications = [
    { label: 'Glass Type', value: 'Toughened / Laminated Glass' },
    { label: 'Glass Thickness', value: '8mm, 10mm, 12mm' },
    { label: 'Frame Options', value: 'Frameless / Aluminium Framed' },
    { label: 'Finish', value: 'Clear, Frosted, Tinted' },
    { label: 'Hardware', value: 'SS 304 / Premium Accessories' },
    { label: 'Sound Reduction', value: 'Up to 35 dB' },
    { label: 'Customization', value: 'Fully Customizable Designs' },
    { label: 'Warranty', value: 'Up to 5 Years' }
  ];

  return (
    <div className="aluminium-window">

      {/* Hero Section */}
      <section className="aluminium-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">Glass Doors & Glass Partitions</h1>
            <p className="hero-subtitle">
              Elegant, functional, and modern glass solutions that bring openness,
              light, and sophistication to any interior space.
            </p>
          </div>

          <div className="hero-image">
            <img
              src={images[selectedImage]}
              alt="Glass Doors and Partitions"
              className="hero-main-image"
            />
            <div className="image-thumbnails">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Glass system ${index + 1}`}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section">
        <div className="section-header">
          <h2>Why Choose Our Glass Solutions?</h2>
          <p>Designed to enhance space, style, and functionality</p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs */}
      <section className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'types' ? 'active' : ''}`}
            onClick={() => setActiveTab('types')}
          >
            Glass Systems
          </button>

          <button
            className={`tab-btn ${activeTab === 'benefits' ? 'active' : ''}`}
            onClick={() => setActiveTab('benefits')}
          >
            Benefits
          </button>
        </div>

        <div className="tabs-content">

          {activeTab === 'types' && (
            <div className="window-types-grid">
              {glassTypes.map((type, index) => (
                <div key={index} className="window-type-card">
                  <img src={type.image} alt={type.name} />
                  <h4>{type.name}</h4>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'benefits' && (
            <div className="benefits-content">
              <div className="benefit-card">
                <h3>Natural Light Flow</h3>
                <p>Maximizes daylight while maintaining defined spaces.</p>
              </div>
              <div className="benefit-card">
                <h3>Premium Appearance</h3>
                <p>Enhances interior aesthetics with a modern, high-end finish.</p>
              </div>
              <div className="benefit-card">
                <h3>Safety & Strength</h3>
                <p>Manufactured using high-quality toughened safety glass.</p>
              </div>
              <div className="benefit-card">
                <h3>Easy Maintenance</h3>
                <p>Simple cleaning and long-lasting glass clarity.</p>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Transform Your Interior Spaces</h2>
          <p>Contact us for customized glass doors and partition solutions</p>
          <div className="cta-buttons">
            <a href="/contactus">
              <button className="btn-outline">Contact Us</button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Is the glass safe?</h4>
            <p>Yes, we use toughened and laminated safety glass compliant with industry standards.</p>
          </div>
          <div className="faq-item">
            <h4>Can privacy be added?</h4>
            <p>Yes, frosted, tinted, or patterned glass options are available.</p>
          </div>
          <div className="faq-item">
            <h4>Are glass doors durable?</h4>
            <p>Absolutely. High-quality hardware and thick safety glass ensure long-term durability.</p>
          </div>
          <div className="faq-item">
            <h4>Where can glass partitions be used?</h4>
            <p>Offices, showrooms, hospitals, banks, and residential interiors.</p>
          </div>
        </div>
      </section>

    </div>
  );
}

export default GlassDoorGlassPartition;
